/**
 * The reusable config list. Categories and Family both render through it, and the
 * next thing that becomes editable (chore rates, grocery aisles, the rulebook)
 * should too: give it rows, an empty state, and optionally a reorder handler.
 *
 * It owns the parts that are easy to get wrong and boring to repeat:
 *   - loading / empty / error states
 *   - reordering by drag (mouse) AND by ↑ ↓ buttons (touch + keyboard + screen
 *     readers — the buttons are the primary path on a phone, where HTML5 drag
 *     does not fire at all)
 *   - live-region announcements when a row moves
 *   - dimming and labelling deactivated rows
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Button, Card, ErrorNote, IconButton } from './ui';

export interface ConfigTableProps<T> {
  /** null = still loading. [] = genuinely empty. */
  items: T[] | null;
  error: string | null;
  onRetry: () => void;
  keyOf: (item: T) => number;
  /** Used in aria-labels and move announcements: "Move Groceries up". */
  labelOf: (item: T) => string;
  renderRow: (item: T) => ReactNode;
  renderActions: (item: T) => ReactNode;
  /** Deactivated rows are dimmed and cannot be dragged. */
  isInactive?: (item: T) => boolean;
  /** Provide to enable reordering. Receives reorderable ids in their new order. */
  onReorder?: (ids: number[]) => void;
  empty: {
    title: string;
    body: string;
    actionLabel: string;
    onAction: () => void;
  };
}

export function ConfigTable<T>({
  items,
  error,
  onRetry,
  keyOf,
  labelOf,
  renderRow,
  renderActions,
  isInactive,
  onReorder,
  empty,
}: ConfigTableProps<T>) {
  // While a drag is in flight we render our own order so the row follows the
  // pointer; on drop we hand the ids up and go back to rendering the prop.
  const [dragging, setDragging] = useState<T[] | null>(null);
  const [heldId, setHeldId] = useState<number | null>(null);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => setDragging(null), [items]);

  if (error) {
    return <ErrorNote message={error} onRetry={onRetry} />;
  }

  if (items === null) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true" aria-live="polite">
        <span className="sr-only">Loading…</span>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-[72px] animate-pulse rounded-card border border-border bg-surface-sunk"
          />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="flex flex-col items-center gap-3 px-6 py-10 text-center">
        <h3 className="font-display text-lg font-semibold text-ink">{empty.title}</h3>
        <p className="max-w-sm text-sm text-ink-soft">{empty.body}</p>
        <Button variant="primary" onClick={empty.onAction} className="mt-1">
          {empty.actionLabel}
        </Button>
      </Card>
    );
  }

  const rows = dragging ?? items;
  const canMove = (item: T) => Boolean(onReorder) && !isInactive?.(item);
  const movableIndexes = rows.map((r, i) => (canMove(r) ? i : -1)).filter((i) => i >= 0);

  function commit(next: T[], moved?: T) {
    const ids = next.filter(canMove).map(keyOf);
    if (moved) {
      const position = ids.indexOf(keyOf(moved)) + 1;
      setAnnouncement(`${labelOf(moved)} moved to position ${position} of ${ids.length}.`);
    }
    onReorder?.(ids);
  }

  /** Swap with the previous/next *movable* row, so inactive rows never trap it. */
  function move(index: number, direction: -1 | 1) {
    const slot = movableIndexes.indexOf(index);
    const targetSlot = slot + direction;
    if (slot < 0 || targetSlot < 0 || targetSlot >= movableIndexes.length) return;

    const next = [...rows];
    const target = movableIndexes[targetSlot];
    [next[index], next[target]] = [next[target], next[index]];
    setDragging(next);
    commit(next, rows[index]);
  }

  function onDragEnter(index: number) {
    if (heldId === null || !canMove(rows[index])) return;
    const fromIndex = rows.findIndex((r) => keyOf(r) === heldId);
    if (fromIndex < 0 || fromIndex === index) return;

    const next = [...rows];
    const [held] = next.splice(fromIndex, 1);
    next.splice(index, 0, held);
    setDragging(next);
  }

  return (
    <>
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>

      <ul className="flex flex-col gap-2">
        {rows.map((item, index) => {
          const id = keyOf(item);
          const inactive = isInactive?.(item) ?? false;
          const movable = canMove(item);
          const slot = movableIndexes.indexOf(index);

          return (
            <li
              key={id}
              draggable={movable}
              onDragStart={(e) => {
                setHeldId(id);
                e.dataTransfer.effectAllowed = 'move';
                // Firefox refuses to start a drag unless the drag data is set.
                // Without this, drag-to-reorder simply never fires there.
                e.dataTransfer.setData('text/plain', String(id));
              }}
              onDragEnter={() => onDragEnter(index)}
              onDragOver={(e) => e.preventDefault()}
              onDragEnd={() => {
                setHeldId(null);
                if (dragging) commit(dragging, item);
              }}
              className={`flex items-center gap-2 rounded-card border border-border bg-surface p-3 transition-opacity ${
                inactive ? 'opacity-60' : ''
              } ${heldId === id ? 'ring-2 ring-sage' : ''}`}
            >
              {onReorder && (
                <div className="flex shrink-0 flex-col">
                  <IconButton
                    label={`Move ${labelOf(item)} up`}
                    disabled={!movable || slot <= 0}
                    onClick={() => move(index, -1)}
                  >
                    <span aria-hidden="true">↑</span>
                  </IconButton>
                  <IconButton
                    label={`Move ${labelOf(item)} down`}
                    disabled={!movable || slot < 0 || slot >= movableIndexes.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <span aria-hidden="true">↓</span>
                  </IconButton>
                </div>
              )}

              <div className="min-w-0 flex-1">{renderRow(item)}</div>

              <div className="flex shrink-0 items-center gap-1">{renderActions(item)}</div>
            </li>
          );
        })}
      </ul>

      {onReorder && (
        <p className="mt-2 text-xs text-ink-soft">
          Drag a row, or use ↑ ↓, to change the order. It saves as soon as you move it.
        </p>
      )}
    </>
  );
}
